import type { Cell } from "@thai-bingo/shared";
import { state } from "../state";
import { t } from "../i18n/i18n";
import { randomizeChar, revealChar } from "./caller";

// --- Pure functions (unit-testable) ---

export function findCellForChar(board: Cell[][], char: string): { r: number; c: number } | null {
    for (let r = 0; r < board.length; r++) {
        for (let c = 0; c < board[r].length; c++) {
            const cell = board[r][c];
            if (cell.char === char && !cell.marked && !cell.free) {
                return { r, c };
            }
        }
    }
    return null;
}

export function botDelay(min = 300, max = 1500): number {
    return min + Math.random() * (max - min);
}

export function isAllBots(botPlayers: boolean[]): boolean {
    return botPlayers.every(b => b);
}

// --- Ready badge helpers ---

export function setBotReady(pi: number, ready: boolean): void {
    const container = document.getElementById("boards-container");
    if (!container) return;
    const cards = container.querySelectorAll(".board-card");
    const card = cards[pi] as HTMLElement | undefined;
    if (!card) return;
    const header = card.querySelector(".board-header");
    if (!header) return;

    if (ready) {
        card.classList.add("bot-ready");
        if (!header.querySelector(".bot-ready-badge")) {
            const badge = document.createElement("span");
            badge.className = "bot-ready-badge";
            badge.textContent = "✓";
            header.appendChild(badge);
        }
    } else {
        card.classList.remove("bot-ready");
        const badge = header.querySelector(".bot-ready-badge");
        if (badge) badge.remove();
    }
}

export function clearAllBotReady(): void {
    const cards = document.querySelectorAll(".board-card.bot-ready");
    cards.forEach(card => {
        card.classList.remove("bot-ready");
        const badge = card.querySelector(".bot-ready-badge");
        if (badge) badge.remove();
    });
    const info = document.getElementById("bot-ready-info");
    if (info) info.remove();
}

export function updateBotReadyInfo(): void {
    const voicePanel = document.querySelector(".voice-panel");
    if (!voicePanel) return;

    const totalBots = state.botPlayers.filter(b => b).length;
    const readyCount = document.querySelectorAll(".board-card.bot-ready").length;

    let info = document.getElementById("bot-ready-info");

    if (readyCount === 0) {
        if (info) info.remove();
        return;
    }

    if (!info) {
        info = document.createElement("div");
        info.className = "bot-ready-info";
        info.id = "bot-ready-info";
        voicePanel.appendChild(info);
    }

    const allReady = readyCount === totalBots && totalBots > 0;
    info.textContent = allReady
        ? t("allBotsReady")
        : t("botsReady").replace("{n}", String(readyCount)).replace("{total}", String(totalBots));
    info.classList.toggle("all-ready", allReady);
}

// --- Orchestration (side-effectful) ---

let botTimeouts: number[] = [];

export function scheduleBotSelections(): void {
    cancelBotSelections();
    if (!state.pendingChar) return;
    const char = state.pendingChar;

    for (let pi = 0; pi < state.boards.length; pi++) {
        if (!state.botPlayers[pi]) continue;
        const match = findCellForChar(state.boards[pi], char);
        const timerId = window.setTimeout(() => {
            if (match) {
                state.pendingSelections[pi] = { pi, r: match.r, c: match.c };
            }
            setBotReady(pi, true);
            updateBotReadyInfo();
        }, botDelay());
        botTimeouts.push(timerId);
    }
}

export function cancelBotSelections(): void {
    for (const id of botTimeouts) {
        window.clearTimeout(id);
    }
    botTimeouts = [];
}

export function startAutoPlay(): void {
    stopAutoPlay();
    const step = () => {
        if (!state.gameActive) return;
        randomizeChar();
        state.autoPlayTimerId = window.setTimeout(() => {
            if (!state.gameActive) return;
            revealChar();
            state.autoPlayTimerId = window.setTimeout(() => {
                if (!state.gameActive) return;
                step();
            }, 1500);
        }, 2000);
    };
    state.autoPlayTimerId = window.setTimeout(step, 500);
}

export function stopAutoPlay(): void {
    if (state.autoPlayTimerId !== null) {
        window.clearTimeout(state.autoPlayTimerId);
        state.autoPlayTimerId = null;
    }
    cancelBotSelections();
}
